import React, { useState, useEffect, useMemo, useRef } from 'react';
// eslint-disable-next-line import/no-unresolved
import { experimental_useObject as useObject } from 'ai/react';
import { z } from 'zod';
import {
  Box,
  IconButton,
  Tooltip,
  useTheme,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Button,
  Paper,
  TextField,
  InputAdornment,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Copy, Languages, Check, Zap, RotateCcw, Search } from 'lucide-mui';

import { RootState } from '../../../../../reducers';
import Segment from './Segment';
import SegmentsEmpty from './SegmentsEmpty';
import Filter from './Filter';
import {
  LanguageCode,
  top50Languages,
  translateSegments,
} from '../../../translate';
import { getCopiableTranscript } from '../../../../../lib/transcriptionHelpers';
import FeedbackModal from '../../../FeedbackModal';
import useLimits from '../../../../plan/hooks/limits';
import { reprocessSelectedVideo } from '../../../selectedVideoSlice';
import HighlightedText from './HighlightedText';
import config from '../../../../../../config';
import { Segment as SegmentType } from '../../../../../types';
import { getToken } from '../../../../../lib/localStorageHandler';
import FadeStaggerCircles from '../../../../../assets/icons/fade-stagger-circles.svg';
import DownloadFile from '../../Download';

type ActionType = 'Copy' | 'Download' | 'Translate' | 'Reprocess';
type FeatureKey = 'copyTranscript' | 'translateTranscript' | 'fullTranscript';

interface ActionObject {
  action: ActionType;
  icon: React.ReactNode;
  label: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  component?: React.ReactNode;
  disabled?: boolean;
}

const aiTranscriptSchema = z.object({
  language: z.string(),
  segments: z.array(
    z.object({
      start: z.string(),
      end: z.string(),
      text: z.string(),
      speaker: z.string(),
      id: z.number(),
    })
  ),
});

type AITranscript = z.infer<typeof aiTranscriptSchema>;

const USE_LLM_FOR_TRANSCRIPTION = true;

const toSeconds = (time: string) => {
  const parts = time.split(':').map(parseFloat);
  let seconds = 0;

  if (parts.length === 3) {
    // h:mm:ss
    seconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
  } else if (parts.length === 2) {
    // mm:ss
    seconds = parts[0] * 60 + parts[1];
  }

  return seconds;
};

const toTime = (seconds: number) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);

  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s
      .toString()
      .padStart(2, '0')}`;
  }

  return `${m}:${s.toString().padStart(2, '0')}`;
};

function enhanceAITranscriptSegments(transcript: AITranscript) {
  return (
    transcript.segments?.map((s, i) => ({
      ...s,
      text: s?.text?.trim() || '',
      id: i + 1,
      start: s?.start ? toSeconds(s.start) : 0,
      end: s?.end ? toSeconds(s.end) : 0,
    })) || []
  );
}

const Segments = () => {
  const theme = useTheme();
  const { selectedVideo } = useSelector((state: RootState) => state);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { object, submit, isLoading } = useObject({
    api: `${config.authBaseUrlV2}/files/actions/${selectedVideo.file._id}/transcribe`,
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
    schema: aiTranscriptSchema,
  });
  const llmTranscript = useMemo(
    () =>
      object ? enhanceAITranscriptSegments(object as AITranscript) : undefined,
    [object]
  );

  const { transcriptRequestedAt, transcriptCompletedAt, speakers } =
    selectedVideo.file?.textData || {};
  const compactSegments = transcriptCompletedAt
    ? selectedVideo.transcript?.compactSegments || []
    : llmTranscript || [];

  const guardLimits = useLimits();
  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [translatedSegments, setTranslatedSegments] = useState<SegmentType[]>(
    []
  );
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const segments =
    translatedSegments.length > 0 ? translatedSegments : compactSegments;

  const loadingImageRef = useRef<HTMLImageElement | null>(null);

  // Add filtering based on selected speaker and notes
  const filteredSegments = useMemo(() => {
    let filtered = segments;

    // Filter by selected speakers if any are selected
    if (selectedVideo.transcript?.selectedSpeakerIds?.length) {
      filtered = filtered.filter((segment) =>
        selectedVideo.transcript.selectedSpeakerIds.includes(
          segment.speaker || ''
        )
      );
    }

    // Filter by notes if showNotesOnly is true
    if (selectedVideo.transcript?.showNotesOnly) {
      const notes = selectedVideo.file?.textData?.notes || [];
      filtered = filtered.filter((segment) => {
        return notes.some(
          (note) =>
            note.timestamp >= segment.start && note.timestamp <= segment.end
        );
      });
    }

    return filtered;
  }, [
    segments,
    selectedVideo.transcript?.selectedSpeakerIds,
    selectedVideo.transcript?.showNotesOnly,
    selectedVideo.file?.textData?.notes,
  ]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const videoElement = document.querySelector('video');
      if (videoElement) {
        setCurrentTime(videoElement.currentTime);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (transcriptRequestedAt && transcriptCompletedAt) {
      const requestedDate = new Date(transcriptRequestedAt);
      const completedDate = new Date(transcriptCompletedAt);

      if (requestedDate > completedDate) {
        setIsReprocessing(true);
      } else {
        setIsReprocessing(false);
      }
    } else {
      setIsReprocessing(false);
    }
  }, [transcriptRequestedAt, transcriptCompletedAt]);

  useEffect(() => {
    if (
      USE_LLM_FOR_TRANSCRIPTION &&
      !transcriptRequestedAt &&
      // XXX Remove file duration conditions when LLM can transcribe files > 12m
      (selectedVideo.file.duration || 0) <= 12 * 60
    ) {
      submit({
        // prompt: 'Please transcribe the video',
        // model: 'fast' // or 'better'
      });
    }
  }, [transcriptRequestedAt]);

  useEffect(() => {
    if (copySuccess) {
      const timer = setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [copySuccess]);

  useEffect(() => {
    if (isLoading && object && loadingImageRef.current) {
      loadingImageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [isLoading, object]);

  if (
    (!USE_LLM_FOR_TRANSCRIPTION ||
      (selectedVideo.file.duration || 0) > 12 * 60) &&
    compactSegments.length === 0
  )
    return <SegmentsEmpty />;

  const handleUpgrade = (featureKey: FeatureKey) => {
    guardLimits(featureKey, () => {
      // Handle upgrade logic here, if needed.
    });
  };

  const handleTranslateClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (guardLimits('translateTranscript')) {
      setAnchorEl(event.currentTarget);
    } else {
      handleUpgrade('translateTranscript');
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTranslateSegments = async (targetLang: LanguageCode) => {
    setIsTranslating(true);

    try {
      const translatedResults = await translateSegments(
        compactSegments,
        targetLang
      );
      setTranslatedSegments(translatedResults);
    } catch (error) {
      console.error('Translation error:', error);
    } finally {
      setIsTranslating(false);
      handleClose();
    }
  };

  const handleCopyTranscript = () => {
    if (guardLimits('copyTranscript')) {
      const copiableTranscript = getCopiableTranscript({
        segments: compactSegments,
      });

      navigator.clipboard
        .writeText(copiableTranscript)
        .then(() => {
          setCopySuccess(true);
        })
        .catch((err) => {
          console.error('Failed to copy text: ', err);
        });
    } else {
      handleUpgrade('copyTranscript');
    }
  };

  const getCopyLabel = () => {
    if (copySuccess) {
      return 'Copied!';
    }
    return 'Copy Transcript';
  };

  const handleFeedbackOpen = () => {
    setFeedbackOpen(true);
  };

  const handleFeedbackClose = () => {
    setFeedbackOpen(false);
  };

  const handleFeedbackSubmit = () => {
    dispatch(reprocessSelectedVideo(selectedVideo.file._id));
    setFeedbackOpen(false);
  };

  const getFullTranscript = () => {
    return segments
      .map(
        (segment) =>
          `[${toTime(segment.start)} - ${toTime(segment.end)}] ${
            speakers?.[segment.speaker]?.name || segment.speaker
          }: ${segment.text}`
      )
      .join('\n\n');
  };

  const actions: ActionObject[] = [
    {
      action: 'Translate',
      icon: <Languages />,
      label: 'Translate Transcript',
      onClick: handleTranslateClick,
      disabled: isTranslating,
    },
    {
      action: 'Reprocess',
      icon: <RotateCcw />,
      label: 'Reprocess',
      onClick: handleFeedbackOpen,
      disabled: isReprocessing,
    },
    {
      action: 'Download',
      icon: (
        <DownloadFile
          text={getFullTranscript()}
          fileName={selectedVideo.file.name || 'transcript'}
          variant="icon"
        />
      ),
      label: 'Download Transcript',
      onClick: () => {}, // The click is handled by the DownloadFile component
    },
    {
      action: 'Copy',
      icon: copySuccess ? <Check /> : <Copy />,
      label: getCopyLabel(),
      onClick: handleCopyTranscript,
      disabled: copySuccess,
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        backgroundColor: theme.palette.background.paper,
        padding: 0,
        borderRadius: theme.spacing(1),
      }}
    >
      {!guardLimits('fullTranscript') && !isLoading && (
        <Alert
          severity="info"
          icon={<Zap />}
          action={
            <Button
              color="secondary"
              variant="outlined"
              size="small"
              onClick={() => handleUpgrade('fullTranscript')}
            >
              Upgrade
            </Button>
          }
          sx={{
            position: 'sticky',
            top: 0,
            width: '100%',
            zIndex: 1,
            marginBottom: theme.spacing(2),
          }}
        >
          Access full Transcripts beyond 5 minutes.
        </Alert>
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: theme.spacing(1),
          marginBottom: theme.spacing(1),
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            flexGrow: 1,
            backgroundColor: theme.palette.background.default,
            borderRadius: theme.spacing(1),
            height: '40px',
            border: '1px solid',
            borderColor: 'divider',
            '& .MuiOutlinedInput-root': {
              height: '100%',
              '& fieldset': {
                borderColor: 'transparent',
              },
              '&:hover fieldset': {
                borderColor: 'transparent',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'transparent',
              },
              '& .MuiInputAdornment-root': {
                '& .MuiSvgIcon-root': {
                  fontSize: '20px',
                  color: 'text.secondary',
                  transition: 'color 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                },
              },
              '&:hover .MuiInputAdornment-root .MuiSvgIcon-root': {
                color: 'text.primary',
              },
            },
          }}
        >
          <TextField
            placeholder="Search transcript..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Paper>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            backgroundColor: theme.palette.background.default,
            borderRadius: theme.spacing(1),
            height: '40px',
            border: '1px solid',
            borderColor: 'divider',
            '& .MuiIconButton-root': {
              width: '28px',
              height: '28px',
              borderRadius: '6px',
              margin: '6px',
              transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                transform: 'translateY(-1px)',
                '& .MuiSvgIcon-root': {
                  transform: 'scale(1.05)',
                },
              },
              '&:active': {
                transform: 'translateY(1px)',
                '& .MuiSvgIcon-root': {
                  transform: 'scale(0.98)',
                },
              },
              '& .MuiSvgIcon-root': {
                fontSize: '18px',
                transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
              },
            },
          }}
        >
          {actions.map((button, index) =>
            button.component ? (
              <Box key={index} sx={{ margin: '4px' }}>
                {button.component}
              </Box>
            ) : (
              <Tooltip key={index} title={button.label} placement="top" arrow>
                <IconButton
                  onClick={button.onClick}
                  disabled={button.disabled}
                  sx={{
                    color:
                      button.action === 'Copy' && copySuccess
                        ? 'success.main'
                        : 'text.primary',
                    '&.Mui-disabled': {
                      opacity: 0.5,
                    },
                  }}
                >
                  {button.icon}
                </IconButton>
              </Tooltip>
            )
          )}
        </Paper>
      </Box>
      <Box sx={{ marginTop: theme.spacing(2) }}>
        <Filter />
      </Box>
      {isTranslating ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <CircularProgress />
        </Box>
      ) : (
        filteredSegments.map((segment, index) => {
          const nextSegment = filteredSegments[index + 1];
          const isCurrentSegment =
            currentTime >= segment.start &&
            (nextSegment ? currentTime < nextSegment.start : true);

          return (
            <Segment
              key={segment.id}
              segment={{
                ...segment,
                id: segment.id.toString(),
                text: (
                  <HighlightedText
                    text={segment.text}
                    searchTerm={searchTerm}
                  />
                ),
              }}
              isCurrentSegment={isCurrentSegment}
              shouldBlur={!guardLimits('fullTranscript') && segment.start > 300}
            />
          );
        })
      )}
      {isLoading && filteredSegments.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            padding: theme.spacing(4),
            textAlign: 'center',
          }}
          ref={loadingImageRef}
        >
          <img
            src={FadeStaggerCircles}
            alt="Transcribing..."
            width={40}
            height={40}
            tabIndex={-1}
          />
          <Typography variant="body1" color="text.secondary">
            Transcribing...
            <br />
            This will take a few seconds for your transcript to appear
          </Typography>
        </Box>
      )}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {top50Languages.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleTranslateSegments(language.code)}
          >
            {language.name}
          </MenuItem>
        ))}
      </Menu>
      <FeedbackModal
        open={feedbackOpen}
        onClose={handleFeedbackClose}
        onSubmit={handleFeedbackSubmit}
      />
    </Box>
  );
};

export default Segments;
